import { getAllEvents, hasEventTranslation } from './data-source';
import { isPublicHoliday } from './event-filters';
import { CALENDAR_YEAR } from './config';
import { getLanguagesForCountry, getLangLabel, getLangLabelEn } from './i18n';
import type { CalendarEvent } from './types';

/** Converts text to a URL-friendly slug */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// --- Lazy cache (same pattern as _slugRegistryCache in i18n.ts) ---

let _slugToEvent: Map<string, CalendarEvent> | null = null;
let _idToSlug: Map<string, string> | null = null;

function ensureBuilt() {
  if (_slugToEvent) return;

  const events = getAllEvents();
  // Don't build (or cache) if events haven't loaded yet — a later call
  // after preloadEvents() completes must be able to rebuild with real data
  // instead of finding a poisoned empty cache.
  if (events.length === 0) return;

  const slugToEvent = new Map<string, CalendarEvent>();
  const idToSlug = new Map<string, string>();
  const slugCounts = new Map<string, number>();

  // First pass: count base slugs to detect collisions
  for (const event of events) {
    const base = `${slugify(event.title)}-${CALENDAR_YEAR}`;
    slugCounts.set(base, (slugCounts.get(base) || 0) + 1);
  }

  // Track which base slugs we've already assigned (for collision disambiguation)
  const usedSlugs = new Set<string>();

  for (const event of events) {
    const base = `${slugify(event.title)}-${CALENDAR_YEAR}`;
    let slug: string;

    if (slugCounts.get(base)! > 1) {
      // Collision — append lowercase country code
      slug = `${base}-${event.country.toLowerCase()}`;
    } else {
      slug = base;
    }

    // Dual-lens dedup: when two events share the same title+country (e.g. Good Friday
    // under both country and religion lens), point the second to the same slug
    if (usedSlugs.has(slug)) {
      const existingEvent = slugToEvent.get(slug);
      if (existingEvent && existingEvent.title === event.title && existingEvent.country === event.country && existingEvent.dateStart === event.dateStart) {
        idToSlug.set(event.id, slug); // alias to same page
        // Prefer the country/public-holiday row as the slug's canonical event so
        // the detail page shows the gazetted classification, not a religion-lens
        // observance (e.g. YE Eid al-Adha). (almanak-j2yp)
        if (isPublicHoliday(event) && !isPublicHoliday(existingEvent)) {
          slugToEvent.set(slug, event);
        }
        continue;
      }
      // Genuine collision — safety fallback
      slug = `${slug}-${event.id}`;
    }

    usedSlugs.add(slug);
    slugToEvent.set(slug, event);
    idToSlug.set(event.id, slug);
  }

  _slugToEvent = slugToEvent;
  _idToSlug = idToSlug;
}

/** Returns the URL slug for a given event ID */
export function getEventSlug(eventId: string): string {
  ensureBuilt();
  return _idToSlug?.get(eventId) || '';
}

/** Returns the event matching a URL slug, or null */
export function getEventBySlug(slug: string): CalendarEvent | null {
  ensureBuilt();
  return _slugToEvent?.get(slug) || null;
}

/**
 * Try progressively shorter versions of a slug to find a matching event.
 * Handles old slugs that had extra trailing segments (e.g. religion-eventId-country).
 * Returns the canonical slug if found, or null.
 */
export function findFallbackEventSlug(slug: string): string | null {
  ensureBuilt();
  if (!_slugToEvent) return null;
  const parts = slug.split('-');
  for (let i = parts.length - 1; i >= 3; i--) {
    const shorter = parts.slice(0, i).join('-');
    if (_slugToEvent.has(shorter)) {
      return shorter;
    }
  }
  return null;
}

/** Returns all event slugs (for generateStaticParams) */
export function getAllEventSlugs(): string[] {
  ensureBuilt();
  return _slugToEvent ? Array.from(_slugToEvent.keys()) : [];
}

/**
 * Returns the languages available for an event — filtered to only those
 * that actually have a translation row in cal_event_translations.
 * English is always included. Used for validation in the translated page route.
 */
export function getEventLangs(eventId: string): string[] {
  ensureBuilt();
  if (!_slugToEvent) return ['en'];
  const event = Array.from(_slugToEvent.values()).find(e => e.id === eventId);
  if (!event) return ['en'];
  return getLanguagesForCountry(event.country)
    .filter(lang => lang === 'en' || hasEventTranslation(eventId, lang));
}

/**
 * Returns alternate language versions of an event page for the nav language
 * selector. Only includes languages with an actual translation (plus English).
 */
export function getAlternateEventLanguages(eventId: string): Array<{ lang: string; slug: string; label: string; labelEn?: string }> {
  ensureBuilt();
  const eventSlug = _idToSlug?.get(eventId);
  if (!eventSlug) return [];
  const event = _slugToEvent?.get(eventSlug);
  if (!event) return [];

  const langs = getLanguagesForCountry(event.country)
    .filter(lang => lang === 'en' || hasEventTranslation(eventId, lang));
  return langs.map(lang => ({
    lang,
    slug: lang === 'en' ? `events/${eventSlug}` : `events/${eventSlug}/${lang}`,
    label: getLangLabel(lang),
    labelEn: getLangLabelEn(lang),
  }));
}

/**
 * Returns all event slug × non-English lang pairs for generateStaticParams.
 * Only emits pairs where a translation actually exists in cal_event_translations —
 * prevents generating ~25k thin/duplicate translated pages that Google flags as
 * duplicate canonicals.
 */
export function getAllEventLangPairs(): Array<{ slug: string; lang: string }> {
  ensureBuilt();
  const pairs: Array<{ slug: string; lang: string }> = [];
  if (!_slugToEvent) return pairs;
  for (const [slug, event] of _slugToEvent) {
    const langs = getLanguagesForCountry(event.country);
    for (const lang of langs) {
      if (lang === 'en') continue;
      if (!hasEventTranslation(event.id, lang)) continue;
      pairs.push({ slug, lang });
    }
  }
  return pairs;
}
